import { useParams } from "react-router";
import InfiniteLoader from "react-window-infinite-loader";
import { useCallback, useEffect, useMemo, useRef } from "react";

import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import NotFoundPage from "components/error/NotFoundPage";
import useErrorHandle from "hooks/useErrorHandle";
import ListEntitiesNew from "components/ListEntitiesNew";
import PaginationIndicator from "components/PaginationIndicator";
import useTitle from "hooks/useTitle";
import PageLoading from "components/loading/PageLoading";
import useBulkActionsSelection from "components/BulkActions/useBulkActionsSelection";
import PrivateWorkersTierInfo from "views/Account/PrivateWorkersTierInfo";
import { useBulkActionsShiftSelection } from "components/BulkActions/useBulkActionsShiftSelection";
import PageInfoHeader from "ds/components/PageInfoHeader";
import Box from "ds/components/Box";

import PrivateWorkerPoolWorkersVirtualizedListItem from "./ListItem/Virtualized";
import useSearchWorkers from "./useSearchWorkers";
import PrivateWorkerPoolHeader from "../Header";
import PrivateWorkerPoolWorkersFiltersLayout from "./FiltersLayout";
import PrivateWorkerPoolWorkersEmpty from "./Empty";
import PrivateWorkerPoolWorkersBulkActions from "./BulkActions";
import { getAllItemsForSelectAll } from "./helpers";

const PrivateWorkerPoolWorkers = () => {
  const { workerPoolId = "" } = useParams<{ workerPoolId: string }>();

  const virtualizedListContainerRef = useRef<HTMLDivElement | null>(null);

  const {
    allSelected,
    selectedSet,
    syncAllSelectedItems,
    selectItem,
    unselectItem,
    onBulkSelectAll,
    onBulkResetAll,
    syncSelectedItemsWithVisibleOnes,
    onBulkContinueWith,
  } = useBulkActionsSelection();

  const {
    workerPool,
    entities,
    loading,
    error,
    loadMoreItems,
    refetch,
    isPageEmpty,
    isPageLoading,
  } = useSearchWorkers(workerPoolId);

  const memoizedWorkersMap = useMemo(
    () => new Map(entities.map((edge) => [edge.id, edge])),
    [entities]
  );

  const isItemLoaded = (value: number) => value < entities.length;

  const onBulkActionsFinish = async () => {
    await refetch();
  };

  const handleBulkSelectAll = useCallback(() => {
    onBulkSelectAll(getAllItemsForSelectAll(entities));
  }, [entities, onBulkSelectAll]);

  useTitle(`Workers · ${workerPool?.name || ""}`);

  useBreadcrumbs(
    [
      {
        title: "Worker pools",
        link: "/worker-pools",
      },
      {
        title: workerPool?.name || "",
      },
    ],
    [workerPool]
  );

  // mark selected new loaded modules if allSelected is true
  useEffect(() => {
    if (allSelected) {
      syncAllSelectedItems(getAllItemsForSelectAll(entities));
    }
  }, [allSelected, entities, syncAllSelectedItems]);

  // sync the selected items with the visible items on the list
  useEffect(() => {
    if (selectedSet.size) {
      syncSelectedItemsWithVisibleOnes(memoizedWorkersMap);
    }
  }, [memoizedWorkersMap, selectedSet.size, syncSelectedItemsWithVisibleOnes]);

  const onSelect = useBulkActionsShiftSelection({
    collection: entities,
    selectedIds: selectedSet,
    selectItem,
    unselectItem,
  });

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (isPageLoading) {
    return <PageLoading />;
  }

  if (!workerPool) {
    return <NotFoundPage />;
  }

  return (
    <>
      <PrivateWorkerPoolHeader
        workerPool={workerPool}
        refetchQuery="SearchPrivateWorkerPoolWorkers"
      />

      <PageInfoHeader title="Workers">
        {!isPageEmpty && (
          <PaginationIndicator
            currentCount={entities.length}
            totalCount={workerPool.workersCount}
            loading={loading}
            minimumLoadingDuration={200}
          />
        )}
      </PageInfoHeader>

      <PrivateWorkersTierInfo />

      {!isPageEmpty && (
        <PrivateWorkerPoolWorkersFiltersLayout
          workerPoolId={workerPoolId}
          allSelected={allSelected}
          onSelectAll={handleBulkSelectAll}
          onResetAll={onBulkResetAll}
        />
      )}

      <PrivateWorkerPoolWorkersBulkActions
        virtualizedListContainerRef={virtualizedListContainerRef}
        selectedSet={selectedSet}
        workersMap={memoizedWorkersMap}
        onBulkResetAll={onBulkResetAll}
        onFinish={onBulkActionsFinish}
        onItemDismiss={unselectItem}
        onBulkContinueWith={onBulkContinueWith}
        workerPool={workerPool}
      />

      {isPageEmpty && <PrivateWorkerPoolWorkersEmpty />}

      {entities.length > 0 && (
        <Box direction="column" grow="1">
          <InfiniteLoader
            isItemLoaded={isItemLoaded}
            itemCount={workerPool.workersCount}
            loadMoreItems={loadMoreItems}
          >
            {({ onItemsRendered }) => (
              <ListEntitiesNew
                itemCount={entities.length}
                itemProps={{
                  items: entities,
                  workerPool,
                  selectedSet,
                  onCheckItem: onSelect,
                }}
                virtualizedItem={PrivateWorkerPoolWorkersVirtualizedListItem}
                itemKey={(index) => entities[index].id}
                onItemsRendered={onItemsRendered}
                outerContainerRef={virtualizedListContainerRef}
              />
            )}
          </InfiniteLoader>
        </Box>
      )}
    </>
  );
};

export default PrivateWorkerPoolWorkers;
